import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import 'react-photo-view/dist/react-photo-view.css';
import './App.css';
import './utils/axiosConfig';
import SplashScreen from './components/SplashScreen';
import PageNotFound from './components/PageNotFound';
import ImageService from './services/ImageService';

// Lazy load components
const HomePage = lazy(() => import('./pages/HomePage'));
const Contact = lazy(() => import('./pages/Contact'));
const NewsAndAnnouncement = lazy(() => import('./pages/NewsAndAnnouncement'));
const Gallery = lazy(() => import('./pages/Gallery'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const Events = lazy(() => import('./pages/Events'));
const StakeHolders = lazy(() => import('./pages/StakeHolders'));
const Support = lazy(() => import('./pages/Support'));
const StartupProfile = lazy(() => import('./components/About/StartupProfile'));

// Loading component
const RouteLoader = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
  </div>
);


function App() {
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [initialDelayPassed, setInitialDelayPassed] = useState(false);

  useEffect(() => {
    const minDisplayTimer = setTimeout(() => {
      setInitialDelayPassed(true);
    }, 2500);

    const loadResources = async () => {
      await new Promise(resolve => setTimeout(resolve, 250));

      try {
        // eslint-disable-next-line no-unused-vars
        const result = await ImageService.loadAllImages();
        setImagesLoaded(true);
      } catch (error) {
        setImagesLoaded(true);
      }
    };

    loadResources();

    return () => {
      clearTimeout(minDisplayTimer);
    };
  }, []);

  const ScrollToTop = () => {
    const { pathname } = useLocation();

    React.useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);

    return null;
  };

  const showMainApp = imagesLoaded && initialDelayPassed;

  return (
    <div className="App h-full">
      {!showMainApp && <SplashScreen />}

      {showMainApp && (
        <div className="app-content-container">
          <HelmetProvider>
            <BrowserRouter>
              <ScrollToTop />
              <Suspense fallback={<RouteLoader />}>
                <Routes>
                  <Route path='/' element={<HomePage />} />
                  <Route path='/contact' element={<Contact />} />
                  <Route path='/gallery' element={<Gallery />} />
                  <Route path='/news&announcement' element={<NewsAndAnnouncement />} />
                  <Route path='/about/:info' element={<AboutPage />} />
                  <Route path='/events' element={<Events />} />
                  <Route path='/stakeHolders/:info' element={<StakeHolders />} />
                  <Route path='/support/:info' element={<Support />} />
                  <Route path="/about/startups/:id" element={<StartupProfile />} /> 
                  {/* <Route path="/admin" element={ <AdminRedirect />}/> */}
                  <Route path='*' element={<PageNotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </HelmetProvider>
        </div>
      )}
    </div>
  );
}

export default App;
